import { useEffect, useState } from 'react';
import { NudosAddressSearchBar, NudosButton, NudosPhoneInput, NudosTextInput } from '../../../NudosComponents';
import NudosCountrySelect from '../../NudosCountrySelect/NudosCountrySelect';
import { countriesList as getCountriesList } from '../../../../services/countriesList';
import { ICountryDetail } from '../../../../types/countries';
import { getAddressErrorText, isPhoneValid } from '../../../../utils/formValidations';
import {
  countriesRequiringPersonalId,
  getIsInvalidZipCode,
  invalidZipCodeText,
  validPersonalIdByCountry,
  validPersonalIdExampleByCountry
} from '../../../../utils/productDefinitions';
import { NudosCityDropdown } from '../..';
import { IcityDetails } from '../../../../types/cities.types';
import { IconInformation, IconWarning } from '../../../../assets/DesignSystem/SVGComponents';
import {
  IShipmentData,
  IorganizationOfficeData,
  IreceiverInformation,
  SHIPMENT_PLATFORM
} from '../../../../types/orders';
import { IaddressFromGoogleMaps } from '../../../../types/global';
import { updateShipmentInformation } from '../../../../services/orders';
import errorMessage from '../../../../utils/errorMessage';
import { displaySuccessNotification } from '../../../../utils/displayNudosStandardNotifications';
import './NudosEditOfficeAddressModal.scss';

/**
 * IMPORTANT: Use this modal as the prop for the CustomModalComponent prop of the NudosModalContent, which in turn must be the modalContent prop of the NudosGenericModal
 * @property { (modalData: IorganizationOfficeData) => void } clickButtonCallback  - a function to execute upon clickin the modal button, receives as param the data of the modal
 * @property { boolean } isButtonLoading - A boolean indicating if the modal submit data button is loading
 */

const NudosEditOfficeAddressModal = ({
  isButtonLoading,
  shipInfo,
  closeModalCallback,
  organizationId,
  updateWithoutLoaders
}: {
  isButtonLoading?: boolean;
  closeModalCallback: () => void;
  shipInfo: IShipmentData;
  organizationId: number;
  updateWithoutLoaders: () => void;
}) => {
  const [city, setCity] = useState<IcityDetails>();
  const [zipCode, setZipCode] = useState<string>();
  const [personalId, setPersonalId] = useState<string>();
  const [receiverName, setReceiverName] = useState<string>();
  const [receiverPhone, setReceiverPhone] = useState<string>();
  const [countriesList, setCountriesList] = useState<ICountryDetail[]>();
  const [selectedCountry, setSelectedCountry] = useState<ICountryDetail>();
  const [selectedAddress, setSelectedAddress] = useState<IaddressFromGoogleMaps>();
  const [loadingCountries, setLoadingCountries] = useState<boolean>(true);
  const [loadingUpdateData, setLoadingUpdateData] = useState(false);
  const [addressAdditionalReferences, setAddressAdditionalReferences] = useState<string>();
  const [phoneCountry, setPhoneCountry] = useState<ICountryDetail>();
  const [wasEdited, setWasEdited] = useState<boolean>(false);

  const defaultCountry = countriesList?.find(country => country.code === shipInfo?.country?.code);
  const defaultPhoneCountry = countriesList?.find(
    countriesList => countriesList.code === shipInfo?.receiverInfo?.receiverPhoneCountryCode
  );
  const receiverIsFromBrazil = selectedCountry?.code === 'br';
  const countryRequiredId = countriesRequiringPersonalId.includes(selectedCountry?.code || '');
  const personalIdIsRequiredAndCurrentlyEmpty = countryRequiredId && !personalId;
  const invalidPersonalId = !validPersonalIdByCountry(personalId || '', selectedCountry?.code);
  const personalIdExample = validPersonalIdExampleByCountry(selectedCountry?.code);
  const blockedByDeliveryProcess = shipInfo.statusId === 4;

  const cityDetails: IcityDetails = {
    id: shipInfo.cityId,
    name: shipInfo?.city,
    country: selectedCountry
  };

  const cancelLsMainText = '   No puedes editar esta orden porque está en proceso de entrega.';

  const alertText = 'Al actualizar la información, esta se guardará en la información de oficina';

  const addressErrorText = getAddressErrorText(
    selectedCountry?.code,
    selectedCountry?.name,
    selectedAddress?.country || undefined
  );

  const getPersonalIdLabel = () => {
    if (receiverIsFromBrazil) return 'Doc. de identidad (CPF)*';
    return 'Doc. de identidad (opcional)';
  };

  const getPersonalIdPlaceholder = () => {
    if (receiverIsFromBrazil) return 'CPF';
    return 'Doc. de identidad';
  };

  const getPersonalIdFieldError = () => {
    if (!receiverName || !receiverPhone) return;
    if (personalIdIsRequiredAndCurrentlyEmpty) return 'Campo obligatorio para este país';
    const documentExample = personalIdExample ? `: ${personalIdExample}` : '';
    if (invalidPersonalId) return `El documento no es válido${documentExample}`;
    return undefined;
  };

  const getZipCodeError = () => {
    if (!zipCode) return ' ';
    if (getIsInvalidZipCode(zipCode)) return invalidZipCodeText;
  };

  const getIsButtonDisabled = () => {
    if (!selectedCountry || !zipCode || !addressAdditionalReferences) return true;
    if (!wasEdited) return true;
    if (!receiverName || !receiverPhone) return true;
    if (personalIdIsRequiredAndCurrentlyEmpty || invalidPersonalId) return true;
    if (addressErrorText) return true;
    if (!!getPhoneError()) return true;
    if (getIsInvalidZipCode(zipCode)) return true;
    return false;
  };

  const getCountries = async () => {
    try {
      setLoadingCountries(true);
      const dataCountries = await getCountriesList();
      setCountriesList(dataCountries);
    } catch {
    } finally {
      setLoadingCountries(false);
    }
  };

  const getPhoneError = () => {
    if (receiverPhone && receiverPhone.length > 3 && !isPhoneValid(receiverPhone)) return 'Ingresa un número válido';
    return undefined;
  };

  const getInitialData = () => {
    if (loadingCountries) return;
    if (!selectedCountry) setSelectedCountry(defaultCountry);
    if (!city) setCity(cityDetails);
    if (!addressAdditionalReferences) setAddressAdditionalReferences(shipInfo?.additionalReferences || undefined);
    if (!zipCode) setZipCode(shipInfo?.zipCode?.trim().replaceAll(' ', '') || undefined);
    if (!receiverPhone) setReceiverPhone(shipInfo?.receiverInfo?.receiverPhone);
    if (!receiverName) setReceiverName(shipInfo?.receiverInfo?.receiverName);
    if (!personalId) setPersonalId(shipInfo?.receiverInfo?.receiverIdDocument);
    if (!phoneCountry) setPhoneCountry(defaultPhoneCountry);
  };

  const updateOfficeAddress = async () => {
    setLoadingUpdateData(true);
    const addressUpdateBody: IorganizationOfficeData = {
      additionalReferences: addressAdditionalReferences,
      city: city?.name || '',
      cityId: city?.id,
      locationId: Number(shipInfo.locationId),
      zipCode: zipCode,
      address: selectedAddress?.address || undefined,
      coordinates: selectedAddress?.coordinates || undefined,
      countryId: Number(selectedCountry?.id),
      countryCode: selectedCountry?.code,
      country: {
        code: `${selectedCountry?.code || ''}`,
        flag: `${selectedCountry?.flag || ''}`,
        id: Number(selectedCountry?.id),
        name: `${selectedCountry?.name}`
      }
    };

    const officeReceiverInfo: IreceiverInformation = {
      receiverIdDocument: personalId,
      receiverName: receiverName,
      receiverPhone: receiverPhone,
      receiverPhoneCountryCode: phoneCountry?.code || undefined
    };

    try {
      await updateShipmentInformation(
        shipInfo.shipmentGroupId,
        {
          officeInfo: addressUpdateBody,
          onlyOrder: false,
          officeReceiverInfo: officeReceiverInfo,
          platform: SHIPMENT_PLATFORM.SOGA
        },
        organizationId
      );

      await updateWithoutLoaders();
      closeModalCallback();
      displaySuccessNotification({
        customJSXMessage: <>Actualizaste la información del envío con éxito</>
      });
    } catch (error) {
      errorMessage();
    } finally {
      setLoadingUpdateData(false);
    }
  };

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipInfo, loadingCountries]);

  return (
    <div className="nudosEditOfficeAddressModal">
      <h2 className="subtitle">
        Estás a punto de editar y notificar al cliente que editaste la información <br />
        de destino
      </h2>
      <section className="officeSection">
        <div className="lineInputs">
          <NudosCountrySelect
            label="País"
            errorText={selectedCountry ? undefined : ' '}
            listOfCountries={[]}
            defaultCountry={selectedCountry}
            handleCountryChange={setSelectedCountry}
            componentSize="extraSmall"
            isDisabled
          />
          <NudosCityDropdown
            countryId={selectedCountry?.id}
            countryCode={selectedCountry?.code}
            currentValue={city}
            componentSize="small"
            isDeactivated={blockedByDeliveryProcess}
            showSkeletonLoader={loadingCountries}
            updateCurrentValueCallback={e => {
              setCity(e);
              setWasEdited(true);
            }}
          />
          <NudosTextInput
            label="Indicaciones"
            isFilled={!!addressAdditionalReferences}
            placeholder="Indicaciones"
            handleChange={e => {
              setAddressAdditionalReferences(e);
              setWasEdited(true);
            }}
            defaultValue={addressAdditionalReferences}
            componentSize="small"
            isDeactivated={blockedByDeliveryProcess}
            showSkeletonLoader={loadingCountries}
          />
        </div>
        <div className="lineInputs">
          <NudosTextInput
            label="CP"
            isFilled={!!zipCode}
            handleBlur={newZipCode => setZipCode(newZipCode?.trim().replaceAll(' ', ''))}
            errorText={getZipCodeError()}
            placeholder="CP"
            defaultValue={zipCode}
            handleChange={e => {
              setZipCode(e);
              setWasEdited(true);
            }}
            componentSize="extraSmall"
            isDeactivated={blockedByDeliveryProcess}
            showSkeletonLoader={loadingCountries}
          />
          <NudosAddressSearchBar
            label="Dirección"
            errorText={addressErrorText}
            isDisabled={blockedByDeliveryProcess}
            regionBias={selectedCountry?.code}
            componentSize="large"
            showSkeletonLoader={loadingCountries}
            handleAddressSelection={e => {
              setSelectedAddress(e);
              setWasEdited(true);
            }}
            didntFindAddressTooltipTopPosition={56 + (addressErrorText ? 10 : 0)}
            didntFindAddressTooltipRightPosition={0}
            defaultValueAddressName={selectedAddress?.address || shipInfo?.deliveryAddress || undefined}
          />
        </div>
      </section>

      <section className="receiverSection">
        <div className="sectionTitle">Quién recibe la herramienta </div>
        <div className="personalIdWarning">El documento de identidad es obligatorio solo para Brasil. </div>
        <div className="lineInputs">
          <NudosTextInput
            label="Nombre y apellido"
            isFilled={!!receiverName}
            errorText={!receiverName ? '' : undefined}
            placeholder="Nombre y apellido"
            handleChange={e => {
              setWasEdited(true);
              setReceiverName(e);
            }}
            defaultValue={receiverName}
            componentSize="small"
            isDeactivated={blockedByDeliveryProcess}
            showSkeletonLoader={loadingCountries}
          />

          <NudosPhoneInput
            label="Número de teléfono"
            errorText={getPhoneError()}
            placeholder="Número de teléfono"
            countriesList={countriesList || []}
            personalClass="phoneInputStyles"
            handlePhoneChange={setReceiverPhone}
            defaultCountryData={phoneCountry}
            defaultPhoneNumber={receiverPhone}
            showSkeletonLoader={loadingCountries}
            handleCountryPhoneChange={setPhoneCountry}
            simpleChangeOfValues={() => {
              setWasEdited(true);
            }}
            isDisabled={loadingUpdateData}
          />

          <NudosTextInput
            label={getPersonalIdLabel()}
            isFilled={!!personalId}
            errorText={getPersonalIdFieldError()}
            placeholder={getPersonalIdPlaceholder()}
            defaultValue={personalId}
            handleChange={e => {
              setWasEdited(true);
              setPersonalId(e);
            }}
            componentSize="small"
            isDeactivated={loadingUpdateData || blockedByDeliveryProcess}
            showSkeletonLoader={loadingCountries}
          />
        </div>
      </section>
      <div className="alertMessage">
        <IconWarning />
        {alertText}
      </div>
      {blockedByDeliveryProcess && (
        <div className="cancelLsContainer">
          <IconInformation className="IconInformation" />
          <div className="cancelLsTextContainer">
            <div>{cancelLsMainText}</div>
          </div>
        </div>
      )}
      <div className="modalButtons">
        <div className="buttonsSubtitle">¿Seguro que deseas continuar?</div>
        <div className="buttonContainer">
          <NudosButton
            customHeight="38px"
            componentSize="small"
            colorPalette="secondary"
            buttonText="Cancelar"
            handleClick={() => {
              closeModalCallback();
            }}
          />
          <NudosButton
            componentSize="small"
            customHeight="38px"
            buttonText="Editar Información"
            isButtonDisabled={getIsButtonDisabled()}
            handleClick={() => updateOfficeAddress()}
            isButtonLoading={isButtonLoading || loadingUpdateData}
          />
        </div>
      </div>
    </div>
  );
};

export default NudosEditOfficeAddressModal;
